import { Trans, useLingui } from '@lingui/react/macro';
import { TrendingUp } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DayLabel } from './day-label';
import { DAY_COLUMNS } from '@/domain/templates/day-of-week';
import {
  toDemandValue,
  withDayValue,
  type DemandForecast,
} from '@/domain/templates/demand-scaling';
import { cn } from '@/lib/utils';
import type { DemandScaling } from '@/types/template';

interface DemandControlProps {
  /** The template's scaling settings — supplies the metric, period and baseline. */
  scaling: DemandScaling;
  /** The transient what-if forecast (defaults each field to the baseline). */
  forecast: DemandForecast;
  onForecastChange: (forecast: DemandForecast) => void;
}

/**
 * The preview's demand playground: a what-if forecast for the template's single
 * metric (a weekly figure or seven daily figures, matching the baseline's
 * period), pre-filled to the baseline so the preview starts at base counts. Each
 * changed field is highlighted; "Reset to baseline" clears the what-if.
 */
export function DemandControl({ scaling, forecast, onForecastChange }: DemandControlProps) {
  const { t } = useLingui();
  const isSales = scaling.metric === 'sales';

  const atBaseline =
    scaling.period === 'daily'
      ? DAY_COLUMNS.every(
          (_, index) => (forecast.daily?.[index] ?? scaling.daily[index]) === scaling.daily[index],
        )
      : (forecast.weekly ?? scaling.weekly) === scaling.weekly;

  function reset() {
    onForecastChange(
      scaling.period === 'daily' ? { daily: [...scaling.daily] } : { weekly: scaling.weekly },
    );
  }

  return (
    <div
      data-testid="demand-control"
      className="space-y-2 rounded-lg border border-primary/20 bg-primary/5 p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs font-medium">
          <TrendingUp className="size-3.5 shrink-0 text-primary" />
          {isSales ? <Trans>Forecast sales</Trans> : <Trans>Forecast guests</Trans>}
          <span className="font-normal text-muted-foreground">
            <Trans>— preview adjusts live</Trans>
          </span>
        </div>
        {!atBaseline && (
          <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={reset}>
            <Trans>Reset to baseline</Trans>
          </Button>
        )}
      </div>

      {scaling.period === 'weekly' ? (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            {isSales && <span className="text-sm text-muted-foreground">£</span>}
            <Input
              type="number"
              min={0}
              className="w-32"
              value={forecast.weekly ?? scaling.weekly}
              onChange={(event) => onForecastChange({ weekly: toDemandValue(event.target.value) })}
              aria-label={isSales ? t`Forecast weekly sales` : t`Forecast weekly guests`}
            />
            <span className="text-xs text-muted-foreground">
              {isSales ? <Trans>per week</Trans> : <Trans>guests per week</Trans>}
            </span>
          </div>
          {(forecast.weekly ?? scaling.weekly) !== scaling.weekly && (
            <p className="text-xs text-muted-foreground">
              <Trans>Baseline: {formatMetric(scaling.weekly, isSales)}</Trans>
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-1.5">
          {DAY_COLUMNS.map((column, index) => {
            const value = forecast.daily?.[index] ?? scaling.daily[index];
            const changed = value !== scaling.daily[index];
            return (
              <div key={column.key} className="space-y-1">
                <div className="text-center text-[length:var(--extra-small-font-size)] leading-[var(--extra-small-line-height)] font-medium text-muted-foreground">
                  <DayLabel columnKey={column.key} />
                </div>
                <Input
                  id={`forecast-day-${column.key}`}
                  type="number"
                  min={0}
                  className={cn('px-1 text-center', changed && 'border-primary')}
                  value={value}
                  onChange={(event) =>
                    onForecastChange({
                      daily: withDayValue(
                        forecast.daily ?? scaling.daily,
                        index,
                        toDemandValue(event.target.value),
                      ),
                    })
                  }
                  aria-label={`${column.key} forecast`}
                />
                {changed && (
                  <div className="text-center text-[length:var(--tiny-font-size)] leading-[var(--tiny-line-height)] text-muted-foreground">
                    <Trans>was {formatMetric(scaling.daily[index], isSales)}</Trans>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Formats a demand figure for display: a `£` prefix for sales, plain otherwise. */
function formatMetric(value: number, isSales: boolean): string {
  return isSales ? `£${value.toLocaleString()}` : value.toLocaleString();
}
